import type {
  ContractsContractVersion,
  ContractsVersionDataField,
  Maybe,
} from 'generated/resources/datatable.graphql'

export type CountryFieldValue = {
  id: string | null
  name: string | null
}

export type CountryFieldName = 'contract_type'

type RelationshipFieldKey = keyof Omit<CountryFieldContractVersion, 'versionData'>

type LegacyFieldConfig = {
  id: keyof CountryFieldContractVersion
  name: keyof CountryFieldContractVersion
  relationship: RelationshipFieldKey
}

export type CountryFieldContractVersion = Pick<
  ContractsContractVersion,
  | 'country'
  | 'esContractTypeId'
  | 'esContractTypeName'
  | 'spainContractType'
  | 'frContractTypeId'
  | 'frContractTypeName'
  | 'franceContractType'
  | 'deContractTypeId'
  | 'deContractTypeName'
  | 'germanyContractType'
  | 'ptContractTypeId'
  | 'ptContractTypeName'
  | 'portugalContractType'
> & {
  versionData?: Maybe<{
    countryData?: Maybe<{
      fields: Array<ContractsVersionDataField>
    }>
  }>
}

type GetCountryFieldParams = {
  isVersionDataEnabled: boolean
  contractVersion: CountryFieldContractVersion | null | undefined
  field: CountryFieldName
}

const LEGACY_FIELD_CONFIG: Record<CountryFieldName, Record<string, LegacyFieldConfig>> = {
  contract_type: {
    es: {
      id: 'esContractTypeId',
      name: 'esContractTypeName',
      relationship: 'spainContractType',
    },
    fr: {
      id: 'frContractTypeId',
      name: 'frContractTypeName',
      relationship: 'franceContractType',
    },
    de: {
      id: 'deContractTypeId',
      name: 'deContractTypeName',
      relationship: 'germanyContractType',
    },
    pt: {
      id: 'ptContractTypeId',
      name: 'ptContractTypeName',
      relationship: 'portugalContractType',
    },
  },
}

const SUPPORTED_COUNTRIES: string[] = ['es', 'fr', 'de', 'pt']

const isSupportedCountry = (country?: string | null): country is string => {
  return Boolean(country && SUPPORTED_COUNTRIES.includes(country))
}

const readLegacyRelationship = (
  contractVersion: CountryFieldContractVersion,
  relationship: RelationshipFieldKey
) => {
  const value = contractVersion[relationship]

  if (!value || typeof value !== 'object') {
    return null
  }

  return value
}

const readLegacyScalar = (
  contractVersion: CountryFieldContractVersion,
  key: keyof CountryFieldContractVersion
) => {
  const value = contractVersion[key]

  if (typeof value === 'string' || typeof value === 'number') {
    return value
  }

  return null
}

const readVersionDataField = (
  contractVersion: CountryFieldContractVersion,
  field: CountryFieldName
): CountryFieldValue | null => {
  const versionDataField = contractVersion.versionData?.countryData?.fields?.find(
    ({ name }) => name === field
  )

  if (!versionDataField) {
    return null
  }

  return {
    id: versionDataField.valueId?.toString() ?? null,
    name: versionDataField.valueLabel ?? null,
  }
}

const readLegacyField = (
  contractVersion: CountryFieldContractVersion,
  field: CountryFieldName
): CountryFieldValue | null => {
  if (!isSupportedCountry(contractVersion.country)) {
    return null
  }

  const fieldConfig = LEGACY_FIELD_CONFIG[field]?.[contractVersion.country]

  if (!fieldConfig) {
    return null
  }

  const legacyRelationship = readLegacyRelationship(contractVersion, fieldConfig.relationship)
  const legacyId = readLegacyScalar(contractVersion, fieldConfig.id)
  const legacyName = readLegacyScalar(contractVersion, fieldConfig.name)

  return {
    id: legacyRelationship?.id?.toString() ?? legacyId?.toString() ?? null,
    name: legacyRelationship?.name ?? legacyName?.toString() ?? null,
  }
}

export const getCountryField = ({
  isVersionDataEnabled,
  contractVersion,
  field,
}: GetCountryFieldParams): CountryFieldValue | null => {
  if (!contractVersion) {
    return null
  }

  if (isVersionDataEnabled) {
    return readVersionDataField(contractVersion, field)
  }

  return readLegacyField(contractVersion, field)
}
